"use client";

import { TextInput } from "@astryxdesign/core/TextInput";
import { Icon } from "@astryxdesign/core/Icon";
import { researchIcons } from "@/lib/icons/research-icons";
import { RuPhoneInput } from "@/components/forms/ru-phone-input";
import { LeadFormMessageField } from "@/components/landing/lead-form-message-field";
import { webmcpParam } from "@/lib/webmcp/forms";

export type LeadFormFieldState = {
  name: string;
  email: string;
  phone: string;
  company: string;
  message: string;
};

export type LeadFormFieldErrors = Partial<
  Record<keyof LeadFormFieldState, string | null>
>;

type LeadFormFieldsProps = {
  values: LeadFormFieldState;
  onChange: <K extends keyof LeadFormFieldState>(
    key: K,
    value: LeadFormFieldState[K],
  ) => void;
  errors?: LeadFormFieldErrors;
  showName?: boolean;
  showEmail?: boolean;
  showPhone?: boolean;
  showCompany?: boolean;
  showMessage?: boolean;
  phoneRequired?: boolean;
  nameRequired?: boolean;
  emailRequired?: boolean;
  companyRequired?: boolean;
  messageRequired?: boolean;
  hasAutoFocus?: boolean;
  onPhoneBlur?: () => void;
  layout?: "stack" | "grid";
};

export function LeadFormFields({
  values,
  onChange,
  errors = {},
  showName = true,
  showEmail = true,
  showPhone = true,
  showCompany = true,
  showMessage = true,
  phoneRequired = false,
  nameRequired = true,
  emailRequired = true,
  companyRequired = false,
  messageRequired = false,
  hasAutoFocus = false,
  onPhoneBlur,
  layout = "grid",
}: LeadFormFieldsProps) {
  const gridClass =
    layout === "grid"
      ? "grid grid-cols-1 gap-3 sm:grid-cols-2"
      : "flex flex-col gap-3";

  return (
    <div className="flex w-full flex-col gap-3">
      <div className={gridClass}>
        {showName ? (
          <TextInput
            label="Имя"
            htmlName="name"
            value={values.name}
            onChange={(v) => onChange("name", v)}
            placeholder="Как к вам обращаться"
            isRequired={nameRequired}
            isOptional={!nameRequired}
            hasAutoFocus={hasAutoFocus}
            startIcon={<Icon icon={researchIcons.users} size="sm" color="secondary" />}
            status={
              errors.name
                ? { type: "error", message: errors.name }
                : undefined
            }
            {...webmcpParam("Имя контактного лица для заявки в CRM")}
          />
        ) : null}
        {showEmail ? (
          <TextInput
            type="email"
            label="Email"
            htmlName="email"
            value={values.email}
            onChange={(v) => onChange("email", v)}
            placeholder="you@company.ru"
            isRequired={emailRequired}
            isOptional={!emailRequired}
            startIcon={<Icon icon={researchIcons.mail} size="sm" color="secondary" />}
            status={
              errors.email
                ? { type: "error", message: errors.email }
                : undefined
            }
            {...webmcpParam("Рабочий email для связи по заявке")}
          />
        ) : null}
        {showPhone ? (
          <div className={showCompany && layout === "grid" ? "" : "sm:col-span-2"}>
            <RuPhoneInput
              value={values.phone}
              onChange={(v) => onChange("phone", v)}
              isRequired={phoneRequired}
              isOptional={!phoneRequired}
              error={errors.phone}
              onBlur={onPhoneBlur}
            />
          </div>
        ) : null}
        {showCompany ? (
          <TextInput
            label="Компания"
            htmlName="company"
            value={values.company}
            onChange={(v) => onChange("company", v)}
            placeholder="Название компании"
            isRequired={companyRequired}
            isOptional={!companyRequired}
            startIcon={<Icon icon={researchIcons.building} size="sm" color="secondary" />}
            {...webmcpParam("Название компании или проекта")}
          />
        ) : null}
      </div>

      {showMessage ? (
        <LeadFormMessageField
          value={values.message}
          onChange={(v) => onChange("message", v)}
          isOptional={!messageRequired}
        />
      ) : null}
    </div>
  );
}
